/**
 * Small localStorage values that let a loading skeleton reserve the geometry
 * a view had last time (panel docked, pinned rows, …) before its queries
 * resolve.
 *
 * Presentation only: values are flags or counts (never paths or content),
 * keyed by a fixed namespace plus the confirmed identity and further scope
 * parts, and must never authorize access or start work. Missing scope or
 * unavailable storage reads as "no hint"; a stale hint costs one layout
 * correction when real data arrives.
 */
export type HintStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function scopedPresentationHintKey(
  namespace: string,
  scopeParts: ReadonlyArray<string | null | undefined>
): string | null {
  const encoded: string[] = [];
  for (const part of scopeParts) {
    if (!part) return null;
    encoded.push(encodeURIComponent(part));
  }
  return [namespace, ...encoded].join(':');
}

export function readScopedPresentationHint(
  storage: HintStorage | null | undefined,
  key: string | null
): string | null {
  if (!storage || !key) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

/** Writes `value`, or removes the key when `value` is null. */
export function writeScopedPresentationHint(
  storage: HintStorage | null | undefined,
  key: string | null,
  value: string | null
): void {
  if (!storage || !key) return;
  try {
    if (value === null) storage.removeItem(key);
    else storage.setItem(key, value);
  } catch {
    // Storage full or unavailable: the hint is an optimization only.
  }
}

export function localStorageOrNull(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}
